import * as React from 'react';
import SessionForm from '../../../../components/forms/SessionForm';
import { Button, Form, Input } from 'antd';
import BusinessModel from '../../models/BusinessModel';
import { RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ScreenNavigationModel, {
  ScreenType
} from '../../models/ScreenNavigationModel';
import { H1 } from '../../../../components/ui/Typography';
import HelpBoxCompanionModel from '../../../../models/HelpBoxCompanionModel';
import { AnimatePresence, motion } from 'framer-motion';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../../components/animations/StepAnimations';
import { useState } from 'react';

interface FCProps { }

const SearchForBusiness: React.FC<FCProps> = ({ }) => {
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();

  const [isVisible, setIsVisible] = useState(true);
  const [nextScreen, setNextScreen] = useState<ScreenType | null>(null);

  const handleScreenChange = async () => {
    setIsVisible(false);
  };

  const helpBoxTitleMessage = t('How do we find your company?');
  const helpBoxParagraphMessage = t('We are looking for your company name or industry at the specified place at Google. If we do not find it, try it with more general - maybe it is different or categorized. Without Google entry you can also enter the data manually.');

  React.useEffect(() => {
    HelpBoxCompanionModel.setDisplayHelpBox(false);
    HelpBoxCompanionModel.setTitleMessage(helpBoxTitleMessage);
    HelpBoxCompanionModel.setParagraphMessage(helpBoxParagraphMessage);
  }, [i18n.language]);

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (nextScreen) {
          setTimeout(() => {
            ScreenNavigationModel.setCurrentScreen(nextScreen);
          }, 200);
        }
      }}>
      {isVisible && (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainerVariants}
          exit="hidden"
        >
          <motion.div variants={fadeInUpVariants} className="w-full mb-[40px]">
            <H1>
              <span style={{ fontWeight: 400 }}>{t("Let's go, ")} </span>
              <span>{t('find your company!')}</span>
            </H1>
          </motion.div>
          <SessionForm
            form={form}
            formName="businessSearchForm"
            formProps={{
              layout: 'vertical',
              initialValues: {
                companyName: BusinessModel.companyName || undefined,
                zipCode: BusinessModel.zipOrCity || undefined
              }
            }}>
            <motion.div variants={fadeInUpVariants} className="flex flex-col gap-5 md:flex-row">
              <Form.Item
                name="companyName"
                className="flex-1 font-bold"
                label={t('Company name')}>
                <Input placeholder={t('Company name or industry of your company')} />
              </Form.Item>
              <Form.Item
                name="zipCode"
                className="flex-1 font-bold"
                label={t('Postal code or city')}>
                <Input placeholder={t('Postal code or city')} />
              </Form.Item>
              <Form.Item
                className="w-auto flex items-end lg:justify-end"
                shouldUpdate>
                {({ getFieldValue }) => {
                  let isDisabled =
                    getFieldValue('companyName') === void 0 ||
                    getFieldValue('companyName')?.trim() === '';

                  return (
                    <motion.div variants={fadeInUpVariants}>
                      <Button
                        id="company-search-next"
                        data-ga-event="company-search-next"
                        loading={BusinessModel.businessLoading}
                        disabled={isDisabled}
                        shape="round"
                        icon={<RightOutlined />}
                        size="large"
                        type="primary"
                        htmlType="submit"
                        style={{ padding: '10px 20px' }}
                        onClick={async () => {
                          BusinessModel.resetSelectedBusiness();
                          const rawName = (getFieldValue('companyName') || '').trim();
                          const rawZip = (getFieldValue('zipCode') || '').trim();
                          BusinessModel.setCompanyName(rawName);
                          BusinessModel.setZipOrCity(rawZip);
                          let searchPath = getFieldValue('companyName');

                          if (
                            getFieldValue('zipCode') !== void 0 &&
                            getFieldValue('zipCode')?.trim() !== ''
                          ) {
                            searchPath += ' zip:' + getFieldValue('zipCode');
                          }

                          await BusinessModel.findBusinessOnGoogle(searchPath);

                          if (BusinessModel.businessList.length === 0) {
                            await BusinessModel.findBusinessOnGMB(searchPath);
                            if (BusinessModel.businessList.length === 0) {
                              setNextScreen(ScreenType.NOT_FOUND_COMPANY);
                            } else {
                              setNextScreen(ScreenType.SELECT_YOUR_COMPANY);
                            }
                            await handleScreenChange();
                          } else {
                            setNextScreen(ScreenType.SELECT_YOUR_COMPANY);
                            await handleScreenChange();
                          }

                        }}
                      ></Button>
                    </motion.div>
                  );
                }}
              </Form.Item>
            </motion.div>
          </SessionForm>
        </motion.div>)}
    </AnimatePresence>
  );
};

export default SearchForBusiness;
